import {useLocation} from "react-router-dom";
import {SiteNavigate} from "nav/SiteLink";
import {useMessages} from "i18n";
import Seo from "../seo/Seo";
import {ADVERTISE_PATH} from "../sponsor/sponsorRoutes";

/**
 * The pre-rename booking path (`/sponsor`), kept alive as a redirect to `/advertise`.
 *
 * A concrete route rather than the `*` catch-all, so the SSG still emits `build/sponsor/index.html`
 * and CloudFront keeps serving a real artifact at that clean URL — links, bookmarks and search results
 * pointing at `/sponsor` reach the booking flow instead of the home page. The redirect itself runs on
 * hydration: effects do not run while pre-rendering, so the artifact is the site chrome with an empty
 * outlet, and the route swap happens as soon as the page is live.
 *
 * <b>The LOCALE TREE is kept.</b> This one component serves the legacy path in every tree, so a bare
 * `/advertise` sent a Romanian advertiser who followed an old `/ro/sponsor` link into the English page.
 * {@link SiteNavigate} is what preserves it: the prefix is read off the address being left rather than
 * from the message catalog, because what is being preserved is the ROUTE the visitor was on, not the
 * language they read. It used to be a `/ro`-only rule written out here, which is one market behind the
 * route table the day a second mirror ships (ui#2498).
 *
 * <b>The QUERY STRING is carried across</b> (kitecorp/transferify-api-service#1241). A bare
 * `to="/advertise"` drops it, and the api's Stripe success URL for a sponsor booking is
 * `<origin>/sponsor?booking=<id>` — so from the 2026-08-17 rename until this fix, every advertiser who
 * paid was redirected to a blank booking form instead of the "Payment received" panel, and the browser
 * had nothing to report the purchase from. Nothing else about the old path may be dropped either: a
 * bookmark carrying `?utm_source=` is an arrival this app attributes.
 *
 * `noindex` is the other half. Without it this URL is an indexable page with no content of its own.
 * `<Seo noindex>` emits `robots: noindex, nofollow` AND withholds the canonical, og:url and hreflang
 * alternates, so the shell claims no identity at all. The booking page it forwards to is `noindex` too
 * (kitecorp/transferify-ui#3610): `/advertising` is the one page for this offer that search should find.
 * Its title and description are the booking page's own: never indexed, but they are what a browser tab
 * and a share preview show in the moment before the redirect lands.
 */
export function Component() {
	const {sponsor} = useMessages();
	const {search, hash} = useLocation();
	return (
		<>
			<Seo title={sponsor.seo.title} description={sponsor.seo.description} noindex/>
			<SiteNavigate to={{pathname: ADVERTISE_PATH, search, hash}} replace/>
		</>
	);
}
